import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { Box, Stack, Typography } from '@mui/material';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EDictionaryTypes, IDictionary, ITranslation } from 'models/dictionary';
import { useQuery } from 'hooks/useQuery';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { ECustomFieldType } from 'models/customField';
import { EDictionaryPrefixTypesUniqueId } from 'helpers/dictionary';
import { EPermission } from 'models/permissions';
import { formatDate, getFullYearsSince } from 'utils/date';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { StyledDivider, StyledSectionTypography } from './styles';
import ImagesGallery from '../ImagesGallery';
import { EModuleType } from '../ImagesGallery/helper';
import { EIconTypes } from 'constants/Icons';
import { EButtonSizesPX } from 'constants/Buttons';
import { ELegalStatus } from 'constants/LegalStatus';
import { ILegalEntityDeactivationWarnings } from 'models/legalEntity';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

export interface ILegalEntityCustomField {
  customFieldName: string;
  customFieldType: string;
  customFieldUniqueId: string;
  value: string;
  translations?: ITranslation[];
}

export interface ILegalEntityData {
  dateOfBirth: string;
  firstName: string;
  lastName: string;
  middleNames?: string;
  secondSurname?: string;
  uniqueId: string;
  visibilityUniqueId: string;
  prefixGroupUniqueId?: string;
  prefixValueUniqueId?: string;
  genderUniqueId?: string;
  hasAssignedUser: boolean;
  displayUniqueId: string;
  customFields?: ILegalEntityCustomField[];
  version: string;
  identityDocumentNumber?: string;
  identityDocumentTypeUniqueId?: string;
  primaryWeaponLicencingAuthorityUniqueId?: string;
  secondaryWeaponLicencingAuthorityUniqueId?: string;
  nationalityUniqueId: string | null;
  placeOfBirth: string | null;
  height: string | null;
  occupation?: string | null;
  [key: string]: any;
  warnings: ILegalEntityDeactivationWarnings;
}

interface ILegalEntityDetailsProps {
  legalEntity: ILegalEntityData;
  pageType?: EDetailsPageTypes;
  handleOpenDeactivate?: () => void;
  handleOpenActivate?: () => void;
  hasManagePermission?: boolean;
}

const LegalEntityDetails = ({
  legalEntity,
  pageType = EDetailsPageTypes.view,
  handleOpenDeactivate,
  handleOpenActivate,
  hasManagePermission,
}: ILegalEntityDetailsProps) => {
  const navigate = useNavigate();
  const query = useQuery();
  const type: string = query.get('type')?.trim() || '';
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const {
    gender,
    weaponLicencingAuthority,
    legalEntityPrefix,
    civilianPrefix,
    militaryPrefix,
    policePrefix,
    nationality,
    loadDictionaries,
    legalVisibility,
    identificationDocumentType,
    legalStatus,
    legalIndividualDeactivationReason,
  } = useLegalEntities();
  const { shortDateFormat, unit, getProperties } = useGlobalProperty();
  const { canManageLE, canDeleteLE } = usePrivilegesValidation();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const isWeaponLicence = hasWeaponLicenceModule();
  const { getFieldsOptional, hiddenFieldsName } = useSystemFieldsConfiguration();

  useEffect(() => {
    getProperties();
    getFieldsOptional();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  enum DetailTranslations {
    editLink = 'legalEntityDetails.edit.link',
  }

  const renderCustomFieldData = (customField: ILegalEntityCustomField) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value;
  };

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.Gender,
        EDictionaryTypes.LegalVisibility,
        EDictionaryTypes.Make,
        EDictionaryTypes.State,
        EDictionaryTypes.legalEntityPrefix,
        EDictionaryTypes.civilianPrefix,
        EDictionaryTypes.militaryPrefix,
        EDictionaryTypes.policePrefix,
        EDictionaryTypes.weaponLicencingAuthority,
        EDictionaryTypes.nationality,
        EDictionaryTypes.IdentificationDocumentType,
        EDictionaryTypes.legalStatus,
        EDictionaryTypes.legalIndividualDeactivationReason,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const renderItemDetail = (dictionary: IDictionary[] | null, dictionaryItemId: string) => {
    const legal: ILegalEntityData = legalEntity;
    if (legal) {
      return (
        dictionary?.find(
          (item: IDictionary) =>
            item?.uniqueId === legal[dictionaryItemId as keyof ILegalEntityData],
        )?.name ?? ''
      );
    }
    return '';
  };

  const renderPrefixDetail = (prefixGroupUniqueId: string | undefined) => {
    switch (prefixGroupUniqueId) {
      case EDictionaryPrefixTypesUniqueId.civilianPrefix:
        return renderItemDetail(civilianPrefix, 'prefixValueUniqueId');
      case EDictionaryPrefixTypesUniqueId.militaryPrefix:
        return renderItemDetail(militaryPrefix, 'prefixValueUniqueId');
      case EDictionaryPrefixTypesUniqueId.policePrefix:
        return renderItemDetail(policePrefix, 'prefixValueUniqueId');
      default:
        return '';
    }
  };

  return (
    <div>
      {legalEntity && (
        <DetailsTemplate
          deletePermissions={[EPermission.DELETE_LE]}
          editPermissions={[EPermission.MANAGE_LE]}
          title={`${
            legalEntity.prefixGroupUniqueId
              ? renderPrefixDetail(legalEntity.prefixGroupUniqueId)
              : ''
          } ${legalEntity.firstName} ${legalEntity.lastName}`}
          pageType={pageType}
          actionButtonFullRow
          actionButtons={[
            ...(hasManagePermission &&
            legalEntity.statusUniqueId === ELegalStatus.INACTIVE &&
            handleOpenActivate
              ? [
                  {
                    icon: EIconTypes.checkmark,
                    sizePX: EButtonSizesPX.px32,
                    onClick: handleOpenActivate,
                  },
                ]
              : []),
            ...(hasManagePermission &&
            legalEntity.statusUniqueId === ELegalStatus.ACTIVE &&
            handleOpenDeactivate
              ? [
                  {
                    icon: EIconTypes.shutDown,
                    sizePX: EButtonSizesPX.px32,
                    onClick: handleOpenDeactivate,
                  },
                ]
              : []),
          ]}
          redirectToEditView={
            type === ELegalEntityTypes.organisation ||
            (type === ELegalEntityTypes.individual &&
              canManageLE(
                legalEntity.primaryWeaponLicencingAuthorityUniqueId,
                legalEntity.secondaryWeaponLicencingAuthorityUniqueId,
              ))
              ? () =>
                  navigate(
                    `${ERouteLinks.EditLegalEntity.replace(':id', id)}?type=${type}&view=true`,
                  )
              : undefined
          }
          redirectToView={
            type === ELegalEntityTypes.organisation ||
            (type === ELegalEntityTypes.individual &&
              canDeleteLE(
                legalEntity.primaryWeaponLicencingAuthorityUniqueId,
                legalEntity.secondaryWeaponLicencingAuthorityUniqueId,
              ))
              ? () =>
                  navigate(`${ERouteLinks.DeleteLegalEntity.replace(':id', id)}?type=${type}`)
              : undefined
          }
          translationsKeys={DetailTranslations}
        >
          <Grid
            container
            sx={{
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              justifyContent: 'center',
              gap: '20px',
            }}
          >
            {type === ELegalEntityTypes.individual &&
              isWeaponLicence &&
              legalEntity &&
              legalEntity.prefixGroupUniqueId ===
                EDictionaryPrefixTypesUniqueId.civilianPrefix && (
                <Grid
                  container
                  sx={{
                    gap: 1,
                    justifyContent: { xs: 'center', sm: 'flex-start', md: 'center' },
                    width: '270px',
                  }}
                >
                  <ImagesGallery
                    moduleType={EModuleType.LE}
                    ownerId={legalEntity.uniqueId}
                    buttonId={'legal_entity_details_individual-add_photo'}
                    buttonPermission={EPermission.MANAGE_LE}
                    onClickEdit={() =>
                      navigate(
                        `${ERouteLinks.LegalEntityEditImages.replace(
                          ':id',
                          id as string,
                        )}?type=${type}`,
                      )
                    }
                    onClickAdd={() =>
                      navigate(
                        `${ERouteLinks.LegalEntityAddImages.replace(
                          ':id',
                          id as string,
                        )}?type=${type}`,
                      )
                    }
                  />
                </Grid>
              )}
            <Grid
              container
              size={{ xs: 12, md: 'auto' }}
              sx={{
                width: {
                  xs: '100%',
                  md:
                    type === ELegalEntityTypes.individual &&
                    isWeaponLicence &&
                    legalEntity &&
                    legalEntity.prefixGroupUniqueId ===
                      EDictionaryPrefixTypesUniqueId.civilianPrefix
                      ? 'calc(100% - 270px)'
                      : '100%',
                },
                rowGap: 1,
                alignContent: 'flex-start',
              }}
            >
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.displayUniqueId.label')}
                  </Typography>
                  <Typography variant="body2">{legalEntity.displayUniqueId}</Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.statusUniqueId.label')}
                  </Typography>
                  <Typography variant="body2">
                    <Chips
                      id="statusUniqueId"
                      text={renderItemDetail(legalStatus, 'statusUniqueId')}
                      variant={
                        legalEntity.statusUniqueId === ELegalStatus.ACTIVE
                          ? EChipsVariants.success
                          : EChipsVariants.error
                      }
                    />
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  {legalEntity.statusUniqueId === ELegalStatus.INACTIVE && (
                    <>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.reasonOfDeactivation.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          renderItemDetail(
                            legalIndividualDeactivationReason,
                            'deactivationReasonUniqueId',
                          ),
                        )}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Grid>

              {type === ELegalEntityTypes.individual && (
                <>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.firstName.label')}
                      </Typography>
                      <Typography variant="body2">{legalEntity.firstName}</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.middleNames.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(legalEntity.middleNames)}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.lastName.label')}
                      </Typography>
                      <Typography variant="body2">{legalEntity.lastName}</Typography>
                    </Stack>
                  </Grid>
                  {!hiddenFieldsName.includes(EFieldsOptional.secondSurname) && (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.secondSurname.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(legalEntity.secondSurname)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.legalEntityType.label')}
                      </Typography>
                      <Box>
                        <Chips
                          id="legalEntityType"
                          text={
                            type === ELegalEntityTypes.individual
                              ? t('legalEntityDetails.legalEntityType.individual.chip.label')
                              : t('legalEntityDetails.legalEntityType.organisation.chip.label')
                          }
                          variant={EChipsVariants.primary}
                        />
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(
                          'legalEntities.individual.accordion.legalEntityTable.prefixGroup.column',
                        )}
                      </Typography>
                      <Typography variant="body2">
                        <Chips
                          id="prefixGroup"
                          text={renderItemDetail(legalEntityPrefix, 'prefixGroupUniqueId')}
                          variant={EChipsVariants.primary}
                        />
                      </Typography>
                    </Stack>
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.visibility.label')}
                  </Typography>
                  <Box>
                    <Chips
                      id="visibility"
                      text={renderItemDetail(legalVisibility, 'visibilityUniqueId')}
                      variant={EChipsVariants.success}
                    />
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <StyledDivider />
                <StyledSectionTypography>
                  {t('legalEntityDetails.generalInformation')}
                </StyledSectionTypography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.dateOfBirth.label')}
                  </Typography>
                  <Typography variant="body2" id="dateOfBirth">
                    {`${formatDate(
                      legalEntity.dateOfBirth,
                      shortDateFormat,
                    )} (${getFullYearsSince(legalEntity.dateOfBirth)} ${t(
                      'legalEntity.yearsOld',
                    )})`}
                  </Typography>
                </Stack>
              </Grid>
              {legalEntity.prefixGroupUniqueId ===
                EDictionaryPrefixTypesUniqueId.civilianPrefix && (
                <>
                  {!hiddenFieldsName.includes(EFieldsOptional.placeOfBirth) && (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.placeOfBirth.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(legalEntity.placeOfBirth)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Grid>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.nationality.label')}
                        </Typography>
                      </Grid>
                      <Typography variant="body2" id="nationalityUniqueId ">
                        {legalEntity.nationalityUniqueId
                          ? renderItemDetail(nationality, 'nationalityUniqueId')
                          : '-'}
                      </Typography>
                    </Stack>
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <Grid>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('legalEntityDetails.gender.label')}
                    </Typography>
                  </Grid>
                  <Typography variant="body2" id="gender">
                    {renderItemDetail(gender, 'genderUniqueId')}
                  </Typography>
                </Stack>
              </Grid>
              {legalEntity.prefixGroupUniqueId ===
                EDictionaryPrefixTypesUniqueId.civilianPrefix && (
                <>
                  {!hiddenFieldsName.includes(EFieldsOptional.height) && (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.height.label')}
                        </Typography>
                        <Typography variant="body2">{`${emptyFieldHolder(legalEntity.height)} ${
                          legalEntity.height ? unit : ''
                        }`}</Typography>
                      </Stack>
                    </Grid>
                  )}
                  {!hiddenFieldsName.includes(EFieldsOptional.occupation) && (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.occupation.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(legalEntity.occupation)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                </>
              )}
              {legalEntity?.customFields?.map((customField) => (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Stack key={customField.customFieldUniqueId}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {customField?.translations?.filter(
                        (translation) =>
                          selectedLanguage?.uniqueId === translation.languageUniqueId,
                      )?.[0]?.name ||
                        customField?.translations?.filter(
                          (translation) =>
                            defaultLanguage?.uniqueId === translation.languageUniqueId,
                        )?.[0]?.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      id={`customField_${customField.customFieldUniqueId}`}
                    >
                      {emptyFieldHolder(renderCustomFieldData(customField))}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
              {legalEntity.prefixGroupUniqueId ===
                EDictionaryPrefixTypesUniqueId.civilianPrefix && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <StyledDivider />
                    <StyledSectionTypography>
                      {t('legalEntityDetails.identification')}
                    </StyledSectionTypography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.identityDocumentType.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          renderItemDetail(
                            identificationDocumentType,
                            'identityDocumentTypeUniqueId',
                          ),
                        )}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.identityDocumentNumber.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(legalEntity.identityDocumentNumber)}
                      </Typography>
                    </Stack>
                  </Grid>

                  {hasWeaponLicenceModule() && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <StyledDivider />
                        <StyledSectionTypography>
                          {t('legalEntityDetails.licencingAuthority')}
                        </StyledSectionTypography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(
                              'legalEntityDetails.primaryWeaponLicencingAuthorityUniqueId.label',
                            )}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              renderItemDetail(
                                weaponLicencingAuthority,
                                'primaryWeaponLicencingAuthorityUniqueId',
                              ),
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(
                              'legalEntityDetails.secondaryWeaponLicencingAuthorityUniqueId.label',
                            )}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              renderItemDetail(
                                weaponLicencingAuthority,
                                'secondaryWeaponLicencingAuthorityUniqueId',
                              ),
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </DetailsTemplate>
      )}
    </div>
  );
};

export { LegalEntityDetails };
